"use client";

import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type {
  User,
  PermissionKey,
  OrganizationRole,
  OrganizationType,
  OrganizationStatus,
  AuthContext,
} from "@/types";
import { queryClient } from "@/lib/query-client";
import { derivePermissions } from "@/utils/permissions";

export type OrgReadiness = "READY" | "NOT_READY";

/** Auth invariant: logout is a hard boundary. No protected component may render after logout. */
export type AuthPhase =
  "BOOTSTRAP" | "AUTHENTICATED" | "UNAUTHENTICATED" | "LOGGING_OUT";

export type OrgBootstrap = {
  hasAcademicYear: boolean;
  hasClassrooms: boolean;
  /** Current academic year has at least one class. Prefer this; accept deprecated alias. */
  hasClassroomsInCurrentYear?: boolean;
  hasClassroomsInActiveYear?: boolean;
};

export type OrganizationContext = {
  id: string;
  name: string;
  type: OrganizationType;
  status?: OrganizationStatus | null;
  /** From backend: READY only when org has current academic year + at least one class. */
  readiness?: OrgReadiness | null;
  /** From backend: drives setup vs dashboard. No /academic-years, /analytics, /audit until hasAcademicYear. */
  bootstrap?: OrgBootstrap | null;
  slug?: string | null;
};

export type AuthState = {
  authPhase: AuthPhase;
  user: User | null;
  org: OrganizationContext | null;
  roles: OrganizationRole[];
  /** Efektivní role aktuálního kontextu (multi-role; = organizationRole v JWT). */
  activeRole: OrganizationRole | null;
  permissions: PermissionKey[];
  context: AuthContext | null;
  loading: boolean;
  authStatus:
    | "anonymous"
    | "authenticating"
    | "authenticated"
    | "refreshing"
    | "unauthenticated";
  offline: boolean;
  hadSession: boolean;
  hydrated: boolean;
  setProfile: (payload: {
    user: User | null;
    org?: OrganizationContext | null;
    /** API /auth/me returns "organization"; mapped to org when org not provided. */
    organization?: OrganizationContext | null;
    roles?: OrganizationRole[];
    activeRole?: OrganizationRole | null;
    permissions?: PermissionKey[];
    context?: AuthContext | null;
  }) => void;
  setOrg: (org: OrganizationContext | null) => void;
  setLoading: (loading: boolean) => void;
  setAuthStatus: (status: AuthState["authStatus"]) => void;
  setOffline: (offline: boolean) => void;
  setHadSession: (hadSession: boolean) => void;
  setHydrated: (hydrated: boolean) => void;
  beginLogout: () => void;
  clearAuthState: () => void;
};

const deriveRoles = (user: User | null): OrganizationRole[] => {
  if (!user) return [];
  const memberships = user.memberships ?? [];
  const roleFromMemberships = memberships.map((membership) => membership.role);
  const roleFromUser = user.organizationRole ? [user.organizationRole] : [];
  const unique = new Set<OrganizationRole>([
    ...roleFromMemberships,
    ...roleFromUser,
  ]);
  return Array.from(unique);
};

// Zustand persists after transient status/loading setters too. Do not write an
// unchanged local snapshot back over a newer identity saved by a sibling tab.
type PersistedAuthState = Pick<AuthState, "user" | "permissions" | "roles" | "activeRole" | "org" | "hadSession" | "context">;
const authStorage = createJSONStorage<PersistedAuthState>(() => {
  const storage = window.localStorage;
  const lastLocalValue = new Map<string, string | null>();
  return {
    getItem(name: string): string | null {
      const value = storage.getItem(name);
      lastLocalValue.set(name, value);
      return value;
    },
    setItem(name: string, value: string): void {
      if (lastLocalValue.get(name) === value) return;
      try {
        storage.setItem(name, value);
        lastLocalValue.set(name, value);
      } catch { /* Session verification also works when storage is unavailable. */ }
    },
    removeItem(name: string): void {
      try { storage.removeItem(name); } catch { /* Best-effort local cache. */ }
      lastLocalValue.set(name, null);
    },
  };
});

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      authPhase: "BOOTSTRAP" as AuthPhase,
      user: null,
      org: null,
      roles: [],
      activeRole: null,
      loading: false,
      authStatus: "anonymous",
      offline: false,
      permissions: [],
      hadSession: false,
      hydrated: false,
      context: null,
      setProfile: ({
        user,
        org,
        organization,
        roles,
        activeRole,
        permissions,
        context,
      }) => {
        const resolvedOrg = org ?? organization ?? null;
        return set(() => ({
          user,
          // Server posílá activeRole v envelope; user.organizationRole je s ní
          // vždy synchronní (fallback pro starší odpovědi).
          activeRole: activeRole ?? user?.organizationRole ?? null,
          org:
            resolvedOrg ??
            (user?.organizationId
              ? (() => {
                  const m = user.memberships?.find(
                    (membership) =>
                      membership.organizationId === user.organizationId,
                  )?.organization;
                  return {
                    id: user.organizationId,
                    name: m?.name ?? "Aktivní organizace",
                    type: (m?.type as OrganizationType | undefined) ?? "SCHOOL",
                    status: m?.status ?? null,
                  };
                })()
              : null),
          roles: roles ?? deriveRoles(user),
          permissions: permissions ?? derivePermissions(user),
          context: context ?? null,
        }));
      },
      setOrg: (org) => set(() => ({ org })),
      setLoading: (loading) => set(() => ({ loading })),
      setAuthStatus: (authStatus) =>
        set((s) => ({
          authStatus,
          authPhase:
            authStatus === "authenticated"
              ? ("AUTHENTICATED" as AuthPhase)
              : authStatus === "unauthenticated"
                ? ("UNAUTHENTICATED" as AuthPhase)
                : s.authPhase,
        })),
      setOffline: (offline) => set(() => ({ offline })),
      setHadSession: (hadSession) => set(() => ({ hadSession })),
      setHydrated: (hydrated) => set(() => ({ hydrated })),
      beginLogout: () =>
        set(() => ({
          authPhase: "LOGGING_OUT" as AuthPhase,
          loading: false,
        })),
      clearAuthState: () =>
        set(() => ({
          authPhase: "UNAUTHENTICATED" as AuthPhase,
          user: null,
          org: null,
          context: null,
          permissions: [],
          roles: [],
          activeRole: null,
          loading: false,
          authStatus: "unauthenticated",
          hadSession: false,
          hydrated: true,
        })),
    }),
    {
      name: "skillstorm_auth",
      storage: authStorage,
      partialize: ({
        user,
        permissions,
        roles,
        activeRole,
        org,
        hadSession,
        context,
      }) => ({
        user,
        permissions,
        roles,
        activeRole,
        org,
        hadSession,
        context,
      }),
      // Do NOT set hydrated or authStatus here. Hydrated is set only after
      // auth bootstrap completes (syncProfile or !hadSession) in useAuth.
      onRehydrateStorage: () => () => {},
    },
  ),
);

// Identity changes are a privacy boundary, including late in-flight responses.
const privateIdentity = (s: AuthState) =>
  JSON.stringify([
    s.user?.id,
    s.org?.id,
    s.activeRole,
    s.context?.mode,
    s.user?.memberships?.find((m) => m.organizationId === s.org?.id)?.id,
    [...s.permissions].sort(),
    s.authPhase === "LOGGING_OUT",
  ]);
useAuthStore.subscribe((next, previous) => {
  if (privateIdentity(next) !== privateIdentity(previous)) queryClient.clear();
});
